import { Link } from "react-router-dom"
import { Badge } from "@/components/ui/badge"
import { buttonVariants } from "@/components/ui/button"
import { BOXED_ROW_CLASS, ROW_BREAK_CLASS, ROW_LAYOUT_CLASS } from "@/components/dashboard/DashboardSection"
import { absoluteTime, relativeTime } from "@/lib/datetime"
import { cn } from "@/lib/utils"
import type { FromMyWorkState } from "@/lib/my-work-row"
import type { MyWorkCommentItem } from "@/stores/my-work-store"

// One row for a comment thread waiting on the current user — the My Work
// "Comments" tab. Same shape as WorkItemRow (label · two lines · action) so the
// tabs read as one list family. The whole row is the link, into the editor
// focused on the comment (the notification deep link).

const REASON_LABEL: Record<MyWorkCommentItem['reason'], string> = {
  mention: 'Mentioned you',
  reply: 'Replied to you',
}

interface CommentWorkRowProps {
  item: MyWorkCommentItem
  linkState?: FromMyWorkState
}

export function CommentWorkRow({ item, linkState }: CommentWorkRowProps) {
  const where = item.sectionTitle ?? 'Whole document'

  return (
    <Link
      to={item.link}
      state={linkState}
      className={cn(ROW_LAYOUT_CLASS, BOXED_ROW_CLASS)}
      data-testid="my-work-comment-row"
    >
      <div className="shrink-0 sm:w-[8.75rem]">
        <Badge variant="secondary" className="whitespace-nowrap font-normal" data-testid="comment-reason">
          {REASON_LABEL[item.reason]}
        </Badge>
      </div>
      {/* Phones: the label on line one, the snippet and the action on line two. */}
      <span aria-hidden="true" className={ROW_BREAK_CLASS} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] font-medium">“{item.snippet}”</p>
        <p className="truncate text-meta mt-0.5">
          {item.actorName} · <span title={absoluteTime(item.at)}>{relativeTime(item.at)}</span>
          {' · '}
          {item.artifactName} · {where}
        </p>
      </div>
      {/* A label for the row's own action, not a second control. */}
      <span
        className={cn(
          buttonVariants({ variant: "outline", size: "sm" }),
          "pointer-events-none min-w-[5.5rem] shrink-0 group-hover:bg-muted",
        )}
      >
        Reply
      </span>
    </Link>
  )
}
